// Internal staff comment thread for a case. Loads once, refreshes off the comment SSE, posts via
// the JSON api. Mirrors the load / live-refresh / mutate shape used across the app.
import { useCallback, useEffect, useState } from 'react';
import { type CaseComment, formatDateTime } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button } from '../../ui/primitives.tsx';

export function CaseComments({ caseId }: { caseId: string }) {
  const can = useCan();
  const [items, setItems] = useState<CaseComment[]>([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<CaseComment[]>(`/cases/${caseId}/comments`).then(setItems).catch(() => setItems([]));
  }, [caseId]);

  useEffect(() => load(), [load]);
  useLiveEvent('case.comment.added', () => load());

  async function post(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/comments`, { body: body.trim() });
      setBody('');
      load();
    } catch {
      setError(t('cases.comments.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {items.length === 0 ? (
        <p className="mb-4 text-sm text-muted">{t('cases.comments.empty')}</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {items.map((c) => (
            <li key={c.id} className="rounded border border-line bg-surface-2 px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-ink">{c.body}</p>
              <p className="mt-1 text-xs text-muted">
                {c.authorName ?? '-'} · {formatDateTime(c.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}
      {can('case:update') && (
        <form className="space-y-2" onSubmit={post}>
          <textarea
            className="w-full rounded border border-line bg-surface px-3 py-2 text-sm"
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('cases.comments.placeholder')}
          />
          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || !body.trim()}>
              {t('cases.comments.post')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
